import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import { apiRequest, type ApiError } from '@/lib/api-client';
import { useSession } from '@/lib/session';

export interface OwnSecurityEvent {
  readonly createdAt: string;
  readonly action: string;
  readonly ipAddress: string | null;
}

export interface OwnSession {
  readonly sessionId: string;
  readonly createdAt: string;
  readonly lastUsedAt: string;
  readonly expiresAt: string;
  readonly revokedAt: string | null;
  readonly revokedReason: string | null;
  readonly ipAddress: string | null;
  readonly userAgent: string | null;
  readonly isActive: boolean;
  readonly isCurrent: boolean;
}

export interface MySecurity {
  readonly previousSignIn: string | null;
  readonly events: readonly OwnSecurityEvent[];
  readonly sessions: readonly OwnSession[];
}

/**
 * `GET /api/v1/auth/me/security` - the signed-in person's OWN history: no id is sent, the API takes the account from the token, so
 * nobody can ask for another account's. No reason is asked: reading one's own history needs no justification.
 */
export function useMySecurityQuery(): UseQueryResult<MySecurity, ApiError> {
  const { accessToken } = useSession();
  return useQuery({
    queryKey: ['my-security'],
    queryFn: () => apiRequest<MySecurity>('/auth/me/security', { accessToken }),
    enabled: accessToken !== null,
    staleTime: 30_000,
  });
}

/** `POST /api/v1/auth/me/sessions/revoke-others` - sign out everywhere else; this session stays. */
export function useRevokeOtherSessionsMutation(): UseMutationResult<
  { sessionsEnded: number },
  ApiError,
  void
> {
  const { accessToken } = useSession();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiRequest<{ sessionsEnded: number }>('/auth/me/sessions/revoke-others', {
        method: 'POST',
        accessToken,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['my-security'] });
    },
  });
}
